/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { faCirclePause } from '@fortawesome/pro-light-svg-icons';
import QuestionsText from './QuestionsText';
import Selector from 'shared/design-system/components/Selector';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactSearchBox from 'shared/design-system/components/TrueImpactSearchBox';
import { SectionBase } from '../SectionBase';
import { getImpactSummaryIcons } from './utils';
import { getHelpText } from './HelperComponents';
import { RadioOptionDetails } from '../RadioGroup';
import { ServiceGroup } from '../ImpactIndicator/utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { ANIMATIONS } from 'shared/design-system/settings/variables/Animation';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { GuidanceTextResponse } from '../utils';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { useProgram } from 'shared/providers/ProgramProvider';

const StyledDiv = styled.div`
  width: 100%;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  .body-text,
  .sub-heading {
    text-align: center;
  }
  .body-text {
    margin-bottom: 24px;
  }
  .not-found {
    text-align: center;
  }
  .rotate {
    .icon-container {
      svg {
        transform: rotate(90deg);
      }
    }
  }
  mark.search-highlight {
    background-color: ${COLORS.warningBackground};
    color: inherit;
    padding: 0 2px;
    border-radius: 2px;
  }
  &.searching {
    .ti-sel-opt {
      position: relative;
      transition: transform 150ms ${ANIMATIONS.easing},
        box-shadow 150ms ${ANIMATIONS.easing},
        outline-color 150ms ${ANIMATIONS.easing};
      &:not(.sel) {
        outline: 2px dashed ${COLORS.ctaPrimarySubdued};
      }
      &:not(.sel) .right-side {
        padding-right: 110px;
      }
      &:not(.sel)::after {
        content: 'Click to select';
        position: absolute;
        top: ${SPACERS.small};
        right: ${SPACERS.small};
        padding: 2px ${SPACERS.tiny};
        border-radius: 12px;
        font-size: 12px;
        font-weight: 600;
        color: ${COLORS.ctaPrimary};
        background-color: ${COLORS.ctaPrimaryBackground};
        pointer-events: none;
        opacity: 0.85;
        transition: opacity 150ms ${ANIMATIONS.easing},
          background-color 150ms ${ANIMATIONS.easing},
          color 150ms ${ANIMATIONS.easing};
      }
      &:not(.sel):hover {
        outline: 2px solid ${COLORS.ctaPrimary};
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(0, 115, 163, 0.18);
        &::after {
          opacity: 1;
          color: ${COLORS.whiteBase};
          background-color: ${COLORS.ctaPrimary};
        }
      }
    }
  }
`;

interface SectionServiceCollectionRenderProps {
  data: ServiceGroup[];
  value?: string;
  questionId: number;
  guidanceText: GuidanceTextResponse | undefined;
  onChange: (questionId: number, value: string) => unknown;
  isProgramLevelQuestion?: boolean;
  disabled?: boolean;
}

interface SectionServiceCollectionProps
  extends SectionServiceCollectionRenderProps {
  title: string;
  subTitle: string;
  error?: any[];
  extraClass?: string;
  intermediaries?: string;
  beneficiaryTerm?: string;
  helpText: string | null;
  answers: Answer[];
  isProgramLevelQuestion?: boolean;
  disabled?: boolean;
}

export const SectionServiceCollectionRender: React.FC<SectionServiceCollectionRenderProps> = ({
  guidanceText,
  questionId,
  data,
  value,
  onChange,
  isProgramLevelQuestion,
  disabled,
}) => {
  const { serviceQuestionId, setService } = useIndicator();
  const { answers: programAnswers } = useProgram();
  const programAnswerText = programAnswers?.[questionId]?.text;
  const seededRef = useRef(false);
  const [search, setSearch] = useState<string>('');
  /* omni-search */
  const extraData = useMemo(() => {
    return !search
      ? data
      : data.filter(
          row =>
            row.name.toLowerCase().includes(search.trim().toLowerCase()) ||
            row.description
              .toLowerCase()
              .includes(search.trim().toLowerCase()) ||
            row.services.filter(ser =>
              ser.name.toLowerCase().includes(search.trim().toLowerCase()),
            ).length > 0 ||
            row.services.filter(ser =>
              ser.description
                .toLowerCase()
                .includes(search.trim().toLowerCase()),
            ).length > 0 ||
            row.services.filter(
              ser =>
                ser.indicators.filter(ind =>
                  ind.name.toLowerCase().includes(search.trim().toLowerCase()),
                ).length > 0 ||
                ser.indicators.filter(ind =>
                  ind.definition
                    .toLowerCase()
                    .includes(search.trim().toLowerCase()),
                ).length > 0,
            ).length > 0,
        );
  }, [data, search]);

  const options = useMemo(() => {
    return extraData?.map(row => row.name);
  }, [extraData]);

  const highlightMatch = useCallback(
    (text: string, term: string): React.ReactNode => {
      const clean = term.trim();
      if (!clean || !text) return text;
      const safe = clean.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const parts = text.split(new RegExp(`(${safe})`, 'ig'));
      return parts.map((part, i) =>
        part.toLowerCase() === clean.toLowerCase() ? (
          <mark key={i} className="search-highlight">
            {part}
          </mark>
        ) : (
          part
        ),
      );
    },
    [],
  );

  const handleSearch = useCallback(
    (ev: any) => {
      setSearch(ev.target.value);
    },
    [setSearch],
  );

  const handleChange = useCallback(
    (value: string) => {
      if (questionId === serviceQuestionId) {
        setService(value);
      }
      onChange(questionId, value);
    },
    [questionId, serviceQuestionId, setService, onChange],
  );

  // One-shot auto-populate from the program-level answer when the report
  // has no answer yet. Field stays editable.
  useEffect(() => {
    if (seededRef.current) return;
    if (!isProgramLevelQuestion) return;
    if (!programAnswerText) return;
    if (value) {
      seededRef.current = true;
      return;
    }
    seededRef.current = true;
    onChange(questionId, programAnswerText);
  }, [isProgramLevelQuestion, programAnswerText, value, questionId, onChange]);

  const handleSearchClear = useCallback(() => {
    setSearch('');
  }, [setSearch]);
  return (
    <>
      <SearchContainer>
        <TrueImpactSearchBox
          value={search}
          onChange={handleSearch}
          placeholder="Health, Education, programming, etc."
          maxWidth="500px"
          margin={`${SPACERS.small} auto 0 auto`}
          autoFocus={true}
          className="service-collection-textfield"
          onClear={handleSearchClear}
          disabledClearButton={search.length === 0}
        />
        <TrueImpactText variant="body-small" align="center" className="helper">
          This will search both <strong>cause</strong> names and descriptions.
        </TrueImpactText>
        {search.length > 0 && (
          <div className="results-callout" role="status">
            <TrueImpactText variant="body-small" className="callout-text">
              Showing {options.length}{' '}
              {options.length === 1 ? 'result' : 'results'} for{' '}
              <strong>“{search}”</strong>
              {options.length > 0 && !disabled && (
                <>
                  {' '}
                  — <strong>click a cause below to select it.</strong>
                </>
              )}
            </TrueImpactText>
          </div>
        )}
      </SearchContainer>
      <StyledDiv className={search.length > 0 && !disabled ? 'searching' : ''}>
        {options.length === 0 ? (
          <TrueImpactText className="not-found">
            No match found. Please try searching for another cause.
          </TrueImpactText>
        ) : (
          <OptionsGroup>
            {options.map((m, i) => (
              <Selector
                key={m + i}
                variant="primary-detailed-radio"
                label={highlightMatch(m, search)}
                icon={getImpactSummaryIcons(m)}
                className={
                  getImpactSummaryIcons(m) === faCirclePause ? 'rotate' : ''
                }
                selected={value === m}
                onClick={() => handleChange(m)}
                disabled={disabled}
                tooltipComponent={
                  <RadioOptionDetails
                    data={extraData[i]}
                    highlight={search.trim()}
                  />
                }
              />
            ))}
          </OptionsGroup>
        )}
      </StyledDiv>
    </>
  );
};

export const SectionServiceCollection: React.FC<SectionServiceCollectionProps> = ({
  data,
  title,
  subTitle,
  helpText,
  guidanceText,
  questionId,
  value,
  beneficiaryTerm,
  intermediaries,
  error,
  extraClass = '',
  answers,
  onChange,
  isProgramLevelQuestion,
  disabled,
}) => {
  const helperText = useMemo(
    () =>
      getHelpText(
        questionId,
        answers,
        helpText || '',
        beneficiaryTerm,
        intermediaries,
      ),
    [helpText, questionId, answers],
  );

  return (
    <SectionBase
      title={title}
      guidanceText={guidanceText}
      error={error as any}
      subTitle={
        <QuestionsText questionId={questionId} defaultTitle={subTitle} />
      }
      questionId={questionId}
      extraClass={extraClass}
      helpText={helperText}
    >
      <SectionServiceCollectionRender
        questionId={questionId}
        data={data}
        guidanceText={guidanceText}
        onChange={onChange}
        value={value}
        isProgramLevelQuestion={isProgramLevelQuestion}
        disabled={disabled}
      />
    </SectionBase>
  );
};

const SearchContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.small};
  align-items: center;
  justify-content: center;
  margin: 0 0 ${SPACERS.extraLarge};
  .helper {
    max-width: 400px;
  }
  .results-callout {
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 500px;
    width: 100%;
    border-radius: 4px;
    padding: ${SPACERS.tiny} ${SPACERS.small};
    border: 1px solid ${COLORS.ctaPrimarySubdued};
    background-color: ${COLORS.ctaPrimaryBackgroundLighter};
    .callout-text {
      margin: 0;
      text-align: center;
    }
  }
`;

const OptionsGroup = styled.div`
  width: 100%;
  display: grid;
  gap: ${SPACERS.medium};
`;
